import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { QUERY_KEYS } from "@/export/api/config.ts";
import {
  listReportJobs,
  createReportJob,
  getReportJob,
  downloadReportArtifact,
} from "@/export/api/requests/reports.requests";
import {
  CreateJobRequest,
  ListJobsQueryParams,
  DownloadQueryParams,
  ReportJob,
  ReportsApiEnvelope,
  DownloadResponse,
} from "@/export/types/reports.ts";

// Query to list report jobs
export const useReportJobs = (params?: ListJobsQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORTS, "jobs", params],
    queryFn: () => listReportJobs(params),
    staleTime: 30000, // 30 seconds
    refetchInterval: 10000, // Refetch every 10 seconds for active jobs
  });
};

// Query to get a specific report job
export const useReportJob = (jobId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORTS, "job", jobId],
    queryFn: () => getReportJob(jobId),
    enabled: enabled && !!jobId,
    staleTime: 5000, // 5 seconds for active monitoring
    refetchInterval: (data) => {
      // Only poll if job is still pending or running
      const job = (data as any)?.data;
      if (job && (job.status === 'PENDING' || job.status === 'RUNNING')) {
        return 5000; // Poll every 5 seconds for active jobs
      }
      return false; // Stop polling for completed jobs
    },
  });
};

// Mutation to create a new report job
export const useCreateReportJob = () => {
  const { t } = useTranslation("export");
  const queryClient = useQueryClient();

  return useMutation<ReportsApiEnvelope<ReportJob>, Error, CreateJobRequest>({
    mutationFn: createReportJob,
    onSuccess: (response) => {
      if (response.data) {
        // Invalidate and refetch jobs list
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.REPORTS, "jobs"],
        });
        
        // Add the new job to cache
        queryClient.setQueryData(
          [QUERY_KEYS.REPORTS, "job", response.data.id],
          response
        );
        
        toast.success(t("toast.jobCreated"));
      }
    },
    onError: (error) => {
      console.error("Error creating report job:", error);
      toast.error(t("toast.jobFailed"));
    },
  });
};

// Mutation to download report artifact
export const useDownloadReportArtifact = () => {
  const { t } = useTranslation("export");

  return useMutation<ReportsApiEnvelope<DownloadResponse>, Error, { jobId: string; params?: DownloadQueryParams }>({
    mutationFn: ({ jobId, params }) => downloadReportArtifact(jobId, params),
    onSuccess: (response) => {
      if (response.data?.url) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = `report_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(t("toast.downloadStarted"));
      }
    },
    onError: (error) => {
      console.error("Error downloading report:", error);
      toast.error(t("toast.downloadFailed"));
    },
  });
};

// Hook to manually trigger download
export const useDownloadReport = () => {
  const downloadMutation = useDownloadReportArtifact();
  
  const downloadReport = (jobId: string, ttl?: number) => {
    downloadMutation.mutate({ 
      jobId, 
      params: ttl ? { ttl } : undefined 
    });
  };

  return {
    downloadReport,
    isDownloading: downloadMutation.isPending,
    downloadError: downloadMutation.error,
  };
};

